// Minimal IndexedDB key/value helpers for persisting File System Access handles
// (which are structured-cloneable) so recent files can be re-opened.

const DB_NAME = 'markroom';
const STORE = 'handles';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error ?? new Error('IndexedDB open failed'));
    };
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      req.onsuccess = () => {
        resolve(req.result);
      };
      req.onerror = () => {
        reject(req.error ?? new Error('IndexedDB request failed'));
      };
    });
  } finally {
    db.close();
  }
}

export function idbGetHandle(id: string): Promise<FileSystemFileHandle | undefined> {
  return withStore<FileSystemFileHandle | undefined>('readonly', (s) =>
    s.get(id) as IDBRequest<FileSystemFileHandle | undefined>,
  );
}

export async function idbPutHandle(id: string, handle: FileSystemFileHandle): Promise<void> {
  await withStore('readwrite', (s) => s.put(handle, id));
}

export async function idbDeleteHandle(id: string): Promise<void> {
  await withStore('readwrite', (s) => s.delete(id));
}
